import * as THREE from 'three';

function mulberry32(seed: number) {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

export function createHomeFieldTexture(seed = 2090): THREE.CanvasTexture {
  const size = 512;
  const random = mulberry32(seed);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Canvas 2D context is unavailable.');

  const soil = context.createLinearGradient(0, 0, size, size);
  soil.addColorStop(0, 'hsl(74 26% 20%)');
  soil.addColorStop(0.46, 'hsl(84 31% 27%)');
  soil.addColorStop(1, 'hsl(44 29% 18%)');
  context.fillStyle = soil;
  context.fillRect(0, 0, size, size);

  // ── CROP ROWS ──────────────────────────────────────────────────────────
  // Wide smooth bezier bands read as abstract stripes rather than as planted
  // ground. Real farmland from the air is a dense, high-frequency corduroy of
  // narrow furrows — the RHYTHM is what says "crop", not the colour.
  const rowStep = 7;
  for (let row = -8; row < size + 8; row += rowStep) {
    const jitter = (random() - 0.5) * 1.4;
    // Lit crest and shadowed trough per furrow. That pair is what makes a flat
    // plane read as ploughed ground.
    context.strokeStyle = 'rgba(150, 168, 92, 0.17)';
    context.lineWidth = 2.4;
    context.beginPath();
    context.moveTo(0, row + jitter);
    context.lineTo(size, row + jitter + (random() - 0.5) * 3);
    context.stroke();

    context.strokeStyle = 'rgba(22, 30, 14, 0.3)';
    context.lineWidth = 1.6;
    context.beginPath();
    context.moveTo(0, row + jitter + 2.6);
    context.lineTo(size, row + jitter + 2.6 + (random() - 0.5) * 3);
    context.stroke();
  }

  // ── FIELD PARCELS ──────────────────────────────────────────────────────
  // Farmland is a patchwork of parcels at slightly different growth stages.
  // Large soft blocks of shifted hue stop the whole plane reading as one flat
  // green, which is the other half of why it did not look like grass.
  for (let i = 0; i < 7; i += 1) {
    const px = random() * size;
    const py = random() * size;
    const pw = 90 + random() * 190;
    const ph = 70 + random() * 160;
    const hue = 68 + random() * 26;
    const light = 18 + random() * 12;
    context.globalAlpha = 0.16 + random() * 0.14;
    context.fillStyle = `hsl(${hue} ${24 + random() * 12}% ${light}%)`;
    context.fillRect(px, py, pw, ph);
  }
  context.globalAlpha = 1;

  // ── GRASS TUFTS ────────────────────────────────────────────────────────
  // Thousands of tiny bright strokes. Individually invisible; collectively
  // they are the difference between "green surface" and "grass".
  for (let i = 0; i < 5200; i += 1) {
    const x = random() * size;
    const y = random() * size;
    const len = 1.5 + random() * 2.6;
    context.strokeStyle = random() > 0.55
      ? 'rgba(176, 196, 108, 0.34)'
      : 'rgba(38, 52, 24, 0.34)';
    context.lineWidth = 0.9;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + (random() - 0.5) * 1.4, y - len);
    context.stroke();
  }

  // Old craters. Grass has grown back in them, so they are value shifts in the
  // surface rather than scars on it — the occupation is 110 days old.
  for (let index = 0; index < 18; index += 1) {
    const x = random() * size;
    const y = random() * size;
    const radius = 6 + random() * 26;
    const crater = context.createRadialGradient(x, y, 0, x, y, radius);
    crater.addColorStop(0, 'rgba(17, 22, 14, .36)');
    crater.addColorStop(0.7, 'rgba(46, 62, 31, .24)');
    crater.addColorStop(1, 'rgba(96, 113, 58, 0)');
    context.fillStyle = crater;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  }

  const pixels = context.getImageData(0, 0, size, size);
  for (let index = 0; index < pixels.data.length; index += 4) {
    const grain = (random() - 0.5) * 13;
    pixels.data[index] += grain;
    pixels.data[index + 1] += grain;
    pixels.data[index + 2] += grain;
  }
  context.putImageData(pixels, 0, 0);

  const texture = new THREE.CanvasTexture(canvas);
  texture.name = 'home-field-crops-2090';
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 8);
  texture.anisotropy = 4;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  return texture;
}
